import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { OrderStatus, Order, BatchedChanges } from '@/types';
import { Button, ConfirmButton } from '@/components/Buttons';
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { approveOrder } from '@/services/orders';
import OrderRejectDialog from '@/seller/components/orders/OrderRejectDialog';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';

type Props = {
    // OwnProps
    order: Order;
    onChange?(changes: BatchedChanges<Order>): void;
};

const ApproveRejectButtons: React.FC<Props> = ({ order, onChange }) => {
    const [savingMode, setSavingMode] = useState<'None' | 'Approving' | 'Rejecting'>('None');
    const [showRejectDialog, setShowRejectDialog] = useState(false);

    if (!order) {
        return null;
    }

    if (order.status === OrderStatus.ReadyToStart) {
        return (
            <Button color="success" className="fill" icon={faCheckCircle} disabled>
                <Trans>Approved</Trans>
            </Button>
        );
    }

    if (order.status === OrderStatus.Rejected) {
        return (
            <Button color="danger" className="fill" icon={faTimesCircle} disabled>
                <Trans>Rejected</Trans>
            </Button>
        );
    }

    if (order.status !== OrderStatus.Pending) {
        return null;
    }

    if (!order.permissions.canApprove) {
        return null;
    }

    const handleRejectClick = () => {
        setShowRejectDialog(true);
    };

    const handleApproveClick = async () => {
        setSavingMode('Approving');

        try {
            await approveOrder(order.id);
            triggerHandler(
                onChange,
                BatchedChanges.repeat<Order>([order.id], { status: OrderStatus.ReadyToStart, rejectReason: null })
            );

            // this.triggerChangeEvent({ status: OrderStatus.ReadyToStart });
        } finally {
            setSavingMode('None');
        }
    };

    const handleRejectDialogAccept = (reason: string) => {
        triggerHandler(
            onChange,
            BatchedChanges.repeat<Order>([order.id], { status: OrderStatus.Rejected, rejectReason: reason })
        );
        // this.triggerChangeEvent({ status: OrderStatus.Rejected });
    };

    return (
        <>
            <OrderRejectDialog
                show={showRejectDialog}
                orderId={order.id}
                onClose={() => setShowRejectDialog(false)}
                onReject={handleRejectDialogAccept}
            />

            <Button
                color="danger"
                tooltip={<Trans>Reject</Trans>}
                onClick={handleRejectClick}
                icon={faTimesCircle}
                loading={savingMode === 'Rejecting'}
                disabled={savingMode !== 'None'}>
                <Trans>Reject</Trans>
            </Button>

            <ConfirmButton
                color="success"
                icon={faCheckCircle}
                tooltip={<Trans>Approve</Trans>}
                onConfirm={handleApproveClick}
                dialogTitle={<Trans>Approve order</Trans>}
                dialogBody={<Trans>Are you sure you would like to approve order {order.orderNumber}?</Trans>}
                loading={savingMode === 'Approving'}
                disabled={savingMode !== 'None'}>
                <Trans>Approve</Trans>
            </ConfirmButton>
        </>
    );
};

export default ApproveRejectButtons;
